import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const AntrimChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Management Antrim | Fibromyalgia Hypnotherapy Market Town</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Antrim from Belfast clinic (20 miles). Neuroscience-based approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Antrim, fibromyalgia hypnotherapy Antrim, pain management market town, back pain treatment Antrim, arthritis pain Antrim" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/antrim-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Management Antrim | Fibromyalgia Hypnotherapy Market Town" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Antrim. Neuroscience-based approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Antrim",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Antrim from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and commuter-related pain with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/antrim-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Antrim",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with chronic back pain from commuting and desk work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for chronic back pain related to commuting and desk work. Many Antrim residents commute to Belfast via the M2 motorway, spending hours behind the wheel and sitting at desks, both of which contribute to chronic back pain, neck pain, and posture issues. Hypnotherapy works by calming the nervous system that amplifies pain signals, reducing muscle tension, improving sleep quality, and helping your brain develop healthier pain processing patterns. The stress of commuting and work deadlines amplifies pain - addressing this stress component is crucial."
                }
              },
              {
                "@type": "Question",
                "name": "How does chronic pain hypnotherapy work alongside NHS care in Antrim?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Antrim clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Antrim for chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 20 miles from Antrim via the M2 motorway, taking around 30 minutes by car in normal traffic. This manageable distance makes regular pain management appointments practical for Antrim residents. Many clients schedule appointments before or after work, making the journey part of their routine commute. The direct M2 route is straightforward and comfortable for those managing chronic pain."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online chronic pain treatment for Antrim residents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Antrim residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, busy parents managing family commitments, or anyone who wants maximum flexibility."
                }
              },
              {
                "@type": "Question",
                "name": "What chronic pain conditions do you treat for Antrim clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Antrim clients experiencing fibromyalgia, chronic back pain (particularly commuter-related and desk work-related), arthritis, migraines, neuropathic pain, repetitive strain injuries from desk work, and IBS pain. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve daily functioning. Many office workers and commuters from Antrim find relief from occupational pain patterns."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for chronic pain management?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your work demands, and your goals. The 4-session package provides excellent value while ensuring commitment to the process."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Antrim
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Antrim from Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">20 miles</span> from Antrim via M2 • <span className="font-semibold">~30 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Online sessions available for Antrim residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Antrim
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Antrim from our Belfast clinic, located approximately 20 miles southeast via the M2 motorway. The half-hour journey is straightforward and manageable for regular pain management appointments, particularly given lengthy NHS waiting times for Northern Trust pain clinic appointments that often exceed 12 months.
              </p>
              <p>
                Living with chronic pain in Antrim's market town community comes with unique pressures. Many residents commute daily to Belfast for office work, spending hours behind the wheel and sitting at desks - a combination that exacerbates back pain, neck pain, and posture issues. Others balance retail work at Junction One or Steeple Rathenraw with family responsibilities, leaving little time for self-care. The constant juggling of work deadlines, commuter stress, parenting demands, and chronic pain creates a perfect storm for nervous system sensitization.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just the injury site. When we're stressed from commuting, work pressures, or family demands, our nervous system becomes hypersensitive and amplifies pain signals. This is where solution-focused hypnotherapy excels. By calming your overactive alarm system, teaching your brain healthier pain processing patterns, and addressing the stress that intensifies pain, many clients experience significant reductions in pain levels and improvements in daily functioning.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand pain conditions from both a clinical and therapeutic perspective, and I know how frustrating it is when you're managing work, family, and chronic pain simultaneously with little support. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you regain control, reduce pain intensity, and improve quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why commuter stress, work deadlines, or parenting pressure can worsen pain intensity significantly.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from commuting, work-life balance struggles, or family demands keeps your survival system on high alert, which keeps pain signals turned up. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways. The more they're used, the easier they fire. But neuroplasticity also means we can create new, healthier pathways through hypnotherapy.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Antrim residents dealing with commuter stress, work-life balance pressures, and the constant juggling of multiple demands.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways discovered in neuroscience research.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during your commute, at your desk, or at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that influence pain levels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. A major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment - outperforming dietary and medication-based approaches.
              </p>
              <p className="text-gray-700 text-lg">
                For Antrim residents waiting months for Northern Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NHS-Complementary Positioning */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, many Antrim residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Chronic Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain, fatigue, and sensitivity. Hypnotherapy addresses the nervous system sensitization underlying fibromyalgia symptoms and helps improve sleep quality.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica, disc problems. Particularly relevant for Antrim commuters and office workers. Calming stress significantly reduces back pain intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Arthritis Pain</h3>
                <p className="text-gray-700">Osteoarthritis and rheumatoid arthritis pain management. Hypnotherapy helps reduce pain perception, improve mobility, and maintain daily activities.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic migraine, tension headaches. Stress reduction and trigger management through hypnotherapy can reduce frequency and intensity significantly.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Desk Work & Posture Pain</h3>
                <p className="text-gray-700">Neck pain, shoulder pain, repetitive strain from desk work. Common among Antrim office workers. Hypnotherapy reduces tension and pain amplification.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Neuropathic Pain</h3>
                <p className="text-gray-700">Nerve pain, burning sensations, numbness. Hypnotherapy works with pain gate control to reduce neuropathic pain signals effectively.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Antrim Clients Choose Sharon Clare for Pain Management
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  With 15 years at Belfast City Hospital, I understand pain conditions from a medical perspective. I've seen firsthand how chronic pain affects patients juggling work and family, and I know the limitations of medication-only approaches. This medical background brings credibility and understanding to chronic pain treatment that many Antrim clients find reassuring.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Commuter & Work-Life Pain
                </h3>
                <p className="text-gray-700">
                  I understand the unique pain patterns facing Antrim residents - the daily M2 commute to Belfast causing back and neck pain, desk work posture issues, the stress of balancing work deadlines with parenting responsibilities. The solution-focused approach addresses both the physical and stress components of modern life pain.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Convenient M2 Access
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is easily accessible via the M2 motorway from Antrim - just around 30 minutes in normal traffic. Many clients schedule appointments before or after work, making pain management fit seamlessly into their commute routine without requiring additional time off.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  If traveling 20 miles is difficult during pain flare-ups, or if you're juggling parenting responsibilities, secure online sessions via video call provide the same professional treatment from your home in Antrim. This flexibility allows busy families to maintain consistent pain management.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Market Town Community Understanding
                </h3>
                <p className="text-gray-700">
                  Understanding the unique challenges of Antrim's market town community - the balance of rural and commuter lifestyles, retail work pressures at Junction One, family demands. The therapeutic approach addresses the whole picture of chronic pain in modern life.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest pain neuroscience research with proven therapeutic techniques. Rather than dwelling on pain history, we focus on solutions, building resilience, and practical strategies for managing pain while maintaining work and family life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Antrim
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M2 motorway into Belfast, then head south to the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 20 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> Around 30 minutes in normal traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By public transport:</span> Botanic and City Hospital are the closest train stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Convenient Commuter Route</h3>
                <p className="text-gray-700">
                  The M2 motorway provides direct, straightforward access from Antrim to Belfast. Many clients schedule appointments before or after work, integrating pain management into their existing commute routine. The journey can be therapeutic - time to transition into a healing mindset away from work and family demands.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition, family commitments, or work schedule, I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Antrim. This option is ideal for those experiencing pain flare-ups, mobility concerns, busy parents, or anyone who prefers online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Antrim Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My chronic back pain from commuting and desk work was unbearable. Sharon's hypnotherapy helped me understand how work stress was making it worse. After 7 sessions, I've reduced my painkillers and I can actually sit at my desk without constant discomfort. The M2 journey was easy to fit around work."
                </p>
                <p className="font-semibold text-gray-900">M.B., Antrim - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Juggling fibromyalgia with parenting and work was impossible. The online sessions were perfect for my busy schedule. Sharon helped me calm my nervous system and the pain levels dropped significantly. I can manage family life now without being overwhelmed by pain. Best decision I made."
                </p>
                <p className="font-semibold text-gray-900">R.K., Antrim - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Waiting 15 months for the pain clinic was unbearable. Hypnotherapy at Belfast gave me tools to manage my arthritis pain while I waited. Scheduling around my commute was easy. My pain hasn't disappeared but it's manageable now, and I feel in control again instead of pain controlling me."
                </p>
                <p className="font-semibold text-gray-900">P.H., Antrim - Arthritis Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Antrim Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with chronic back pain from commuting and desk work?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for chronic back pain related to commuting and desk work. Many Antrim residents commute to Belfast via the M2 motorway, spending hours behind the wheel and sitting at desks, both of which contribute to chronic back pain, neck pain, and posture issues. Hypnotherapy works by calming the nervous system that amplifies pain signals, reducing muscle tension, improving sleep quality, and helping your brain develop healthier pain processing patterns. The stress of commuting and work deadlines amplifies pain - addressing this stress component is crucial for effective pain management.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does chronic pain hypnotherapy work alongside NHS care in Antrim?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Antrim clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments for comprehensive pain management.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Antrim for chronic pain treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 20 miles from Antrim via the M2 motorway, taking around 30 minutes by car in normal traffic. This manageable distance makes regular pain management appointments practical for Antrim residents. Many clients schedule appointments before or after work, making the journey part of their routine commute. The direct M2 route is straightforward and comfortable for those managing chronic pain, and you can use the drive time to practice relaxation techniques learned in sessions.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online chronic pain treatment for Antrim residents?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Antrim residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, busy parents managing family commitments, or anyone who wants maximum flexibility. You can switch between in-person and online sessions based on your needs and schedule.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What chronic pain conditions do you treat for Antrim clients?
                </h3>
                <p className="text-gray-700">
                  I work with Antrim clients experiencing fibromyalgia, chronic back pain (particularly commuter-related and desk work-related), arthritis, migraines, neuropathic pain, repetitive strain injuries from desk work, neck and shoulder pain from poor posture, and IBS pain. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve daily functioning. Many office workers and commuters from Antrim find relief from occupational pain patterns through targeted hypnotherapy.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your work demands, family commitments, and your goals. The 4-session package provides excellent value while ensuring commitment to the process and allowing time for meaningful neuroplastic change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default AntrimChronicPain;
